/* lib/buildWatcher.js — "man it's still how it was" (2026-08-19).
 *
 * A tab left open keeps running the JavaScript it loaded, forever. The server
 * was serving the NEW build (index.html is no-cache, the bundle hash changes
 * every deploy) while the open tab still ran the OLD one — so shipped fixes
 * looked like they never happened.
 *
 * This watcher compares the bundle the page is RUNNING against the bundle the
 * server currently advertises (CRA's asset-manifest.json) and, when they
 * differ, offers a one-click update — auto-applying it after a short
 * countdown when nothing important is in flight (never mid-render/upload:
 * anything long-running sets window.__LHC_BUSY).
 *
 * Plain DOM, no React: it must work even if the app itself is broken.
 */

const POLL_MS = 60_000;
const AUTO_RELOAD_AFTER_MS = 20_000;

function runningBundle() {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;
  const m = /\/static\/js\/main\.[^/"']+\.js/.exec(el.getAttribute("src") || el.src || "");
  return m ? m[0] : null;
}

async function serverBundle() {
  const res = await fetch("/asset-manifest.json?t=" + Date.now(), { cache: "no-store" });
  if (!res.ok) return null;
  const j = await res.json();
  const f = (j && j.files && j.files["main.js"]) || null;
  return f ? String(f).replace(/^https?:\/\/[^/]+/, "") : null;
}

function showBanner(onUpdate) {
  if (document.getElementById("lhc-update-bar")) return;
  const bar = document.createElement("div");
  bar.id = "lhc-update-bar";
  bar.setAttribute("data-testid", "app-update-bar");
  bar.style.cssText = [
    "position:fixed", "left:50%", "top:14px", "transform:translateX(-50%)", "z-index:2147483000",
    "display:flex", "align-items:center", "gap:12px", "padding:10px 14px 10px 16px",
    "border-radius:12px", "background:#141418", "color:#f2efe9",
    "font:600 13px/1.2 Inter, system-ui, sans-serif",
    "box-shadow:0 10px 34px rgba(0,0,0,.35)", "border:1px solid rgba(232,207,154,.35)",
  ].join(";");
  const msg = document.createElement("span");
  msg.textContent = "A new version of LHC Studio is ready";
  const count = document.createElement("span");
  count.style.cssText = "opacity:.6;font-weight:500";
  const btn = document.createElement("button");
  btn.textContent = "Update now";
  btn.setAttribute("data-testid", "app-update-now");
  btn.style.cssText = "padding:7px 14px;border-radius:8px;border:0;background:linear-gradient(90deg,#c9a86c,#e8cf9a);color:#1f1a10;font:700 12.5px Inter, system-ui, sans-serif;cursor:pointer";
  const later = document.createElement("button");
  later.textContent = "Later";
  later.style.cssText = "padding:7px 10px;border-radius:8px;border:1px solid rgba(255,255,255,.22);background:transparent;color:#f2efe9;font:600 12.5px Inter, system-ui, sans-serif;cursor:pointer";
  bar.append(msg, count, btn, later);
  document.body.appendChild(bar);

  let left = Math.round(AUTO_RELOAD_AFTER_MS / 1000);
  let timer = null;
  const stop = () => { if (timer) clearInterval(timer); timer = null; };
  const tick = () => {
    // never interrupt a render, an upload or any long job
    if (window.__LHC_BUSY) { count.textContent = ""; return; }
    left -= 1;
    count.textContent = left > 0 ? "updating in " + left + "s" : "";
    if (left <= 0) { stop(); onUpdate(); }
  };
  timer = setInterval(tick, 1000);
  btn.onclick = () => { stop(); onUpdate(); };
  later.onclick = () => { stop(); bar.remove(); };
}

export function startBuildWatcher() {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  // defensive: an old service worker from any earlier deploy would pin stale
  // assets forever — none is registered today, so this is pure hygiene.
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
    }
  } catch (e) { /* unsupported */ }

  const mine = runningBundle();
  if (!mine) return;                        // dev server / unknown shape → no watcher
  const doUpdate = () => { try { window.location.reload(); } catch (e) { /* blocked */ } };
  const check = async () => {
    try {
      const theirs = await serverBundle();
      if (theirs && theirs !== mine) showBanner(doUpdate);
    } catch (e) { /* offline — try again next tick */ }
  };
  setTimeout(check, 4000);                  // shortly after boot
  setInterval(check, POLL_MS);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) check(); });
  window.addEventListener("focus", check);
}
